import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ReactNode } from "react";

type DialogSize = "sm" | "md" | "lg" | "xl" | "2xl" | "3xl" | "4xl";

const SIZE_CLASS: Record<DialogSize, string> = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  "3xl": "max-w-3xl",
  "4xl": "max-w-4xl",
};

export function Dialog({
  open,
  onOpenChange,
  title,
  description,
  children,
  footer,
  size = "md",
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description?: string;
  children?: ReactNode;
  footer?: ReactNode;
  size?: DialogSize;
}) {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-black/70 backdrop-blur-sm animate-in fade-in" />
        <RadixDialog.Content
          className={cn(
            "fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2",
            "w-[92vw] max-h-[90vh] flex flex-col",
            "rounded-lg border border-[var(--color-border)] bg-[var(--color-panel)] p-5 shadow-2xl",
            "animate-in fade-in zoom-in-95",
            SIZE_CLASS[size]
          )}
        >
          <div className="flex items-start justify-between mb-3 shrink-0">
            <div>
              <RadixDialog.Title className="text-base font-semibold tracking-[-0.24px]">
                {title}
              </RadixDialog.Title>
              {description && (
                <RadixDialog.Description className="text-sm text-[var(--color-fg-subtle)] mt-0.5">
                  {description}
                </RadixDialog.Description>
              )}
            </div>
            <RadixDialog.Close className="text-[var(--color-fg-subtle)] hover:text-[var(--color-fg)] transition-colors">
              <X size={16} />
            </RadixDialog.Close>
          </div>
          {children && (
            <div className="my-4 flex-1 overflow-y-auto -mx-1 px-1">{children}</div>
          )}
          {footer && (
            <div className="flex justify-end gap-2 mt-5 pt-3 border-t border-[var(--color-border)] shrink-0">
              {footer}
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
